<template>
  <div class="bet-stats">
    <!-- Total bets -->
    <div class="stat-card stat-card--orange">
      <div class="stat-card__header">
        <svg class="stat-card__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <span class="stat-card__label">{{ $t('myBets') }}</span>
      </div>
      <div class="stat-card__value">{{ stats.total }}</div>
    </div>

    <!-- Total played -->
    <div class="stat-card stat-card--blue">
      <div class="stat-card__header">
        <svg class="stat-card__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="stat-card__label">{{ $t('totalPlayed') }}</span>
      </div>
      <div class="stat-card__value">{{ stats.currency }} {{ formatPrice(stats.totalPlayed) }}</div>
    </div>

    <!-- Potential winnings -->
    <div class="stat-card stat-card--yellow">
      <div class="stat-card__header">
        <svg class="stat-card__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
        <span class="stat-card__label">{{ $t('potentialWinnings') }}</span>
      </div>
      <div class="stat-card__value">{{ stats.currency }} {{ formatPrice(stats.pending) }}</div>
    </div>

    <!-- Void -->
    <div class="stat-card stat-card--red">
      <div class="stat-card__header">
        <svg class="stat-card__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
        <span class="stat-card__label">{{ $t('void') }}</span>
      </div>
      <div class="stat-card__value">{{ stats.currency }} {{ formatPrice(stats.void) }}</div>
    </div>

    <!-- Total won -->
    <div class="stat-card stat-card--green">
      <div class="stat-card__header">
        <svg class="stat-card__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="stat-card__label">{{ $t('totalWon') }}</span>
      </div>
      <div class="stat-card__value">{{ stats.currency }} {{ formatPrice(stats.winner) }}</div>
    </div>

    <!-- Net -->
    <div class="stat-card stat-card--gray">
      <div class="stat-card__header">
        <svg class="stat-card__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span class="stat-card__label">{{ $t('net') }}</span>
      </div>
      <div class="stat-card__value" :class="stats.net >= 0 ? 'stat-card__value--positive' : 'stat-card__value--negative'">
        {{ stats.currency }} {{ formatPrice(stats.net) }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BetStats',
  props: {
    stats: {
      type: Object,
      required: true
    }
  },
  methods: {
    formatPrice(value) {
      const val = (value / 1).toFixed(2).replace('.', ',');
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
  }
};
</script>

<style scoped>
.bet-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

@media (min-width: 480px) {
  .bet-stats { grid-template-columns: repeat(3, 1fr); }
}

@media (min-width: 768px) {
  .bet-stats { grid-template-columns: repeat(6, 1fr); }
}

.stat-card {
  border-radius: 6px;
  padding: 12px;
  border: 1px solid transparent;
  border-left-width: 4px;
  color: var(--mybets-text);
}

.stat-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.stat-card__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.stat-card__label {
  font-size: var(--font-size-2xl);
}

.stat-card__value {
  font-size: var(--font-size-7xl);
  font-weight: 700;
}

.stat-card__value--positive { color: #22c55e; }
.stat-card__value--negative { color: #ef4444; }

/* Color variants */
.stat-card--orange { background-color: #fff7ed; border-color: #ea580c; }
.stat-card--orange .stat-card__icon { color: #ea580c; }

.stat-card--blue { background-color: #eff6ff; border-color: #2563eb; }
.stat-card--blue .stat-card__icon { color: #2563eb; }

.stat-card--yellow { background-color: #fefce8; border-color: #ca8a04; }
.stat-card--yellow .stat-card__icon { color: #ca8a04; }

.stat-card--red { background-color: #fef2f2; border-color: #991b1b; }
.stat-card--red .stat-card__icon { color: #991b1b; }

.stat-card--green { background-color: #f0fdf4; border-color: #16a34a; }
.stat-card--green .stat-card__icon { color: #16a34a; }

.stat-card--gray { background-color: #f9fafb; border-color: #6b7280; }
.stat-card--gray .stat-card__icon { color: #6b7280; }
</style>
